// src/views/whatsappInbox/components/InitialsAvatar.tsx
import React, { memo } from 'react';

interface InitialsAvatarProps {
  name?: string;
  phone?: string;
  size?: number;
  fontSize?: number;
  style?: React.CSSProperties;
}

/**
 * Generates a consistent color based on a string (name or phone)
 * Uses a hash function to ensure same input always gives same color
 */
const stringToColor = (str: string): string => {
  if (!str) return '#94a3b8'; // Default slate color

  // Hash function
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  }

  // Vibrant color palette - avoiding grey tones
  const colors = [
    '#ef4444', // red
    '#f97316', // orange
    '#f59e0b', // amber
    '#eab308', // yellow
    '#84cc16', // lime
    '#22c55e', // green
    '#10b981', // emerald
    '#14b8a6', // teal
    '#06b6d4', // cyan
    '#0ea5e9', // sky
    '#3b82f6', // blue
    '#6366f1', // indigo
    '#8b5cf6', // violet
    '#a855f7', // purple
    '#d946ef', // fuchsia
    '#ec4899', // pink
    '#f43f5e', // rose
  ];

  const index = Math.abs(hash) % colors.length;
  return colors[index];
};

/**
 * Extracts initials from a name
 * Examples: "John Doe" → "JD", "John" → "JO", "+919876543210" → "+91"
 */
const getInitials = (name?: string, phone?: string): string => {
  if (name && name.trim()) {
    const parts = name.trim().split(' ').filter(Boolean);

    if (parts.length >= 2) {
      // First letter of first and last name
      return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    } else if (parts.length === 1 && parts[0].length >= 2) {
      // First two letters of single name
      return parts[0].substring(0, 2).toUpperCase();
    } else if (parts.length === 1) {
      // Single letter name
      return parts[0][0].toUpperCase();
    }
  }

  // Fallback to phone number
  if (phone) {
    // For phone numbers, show country code or first 2 digits
    const cleaned = phone.replace(/\D/g, '');
    if (phone.startsWith('+')) {
      return phone.substring(0, 3); // "+91"
    }
    return cleaned.substring(0, 2);
  }

  return '?';
};

/**
 * InitialsAvatar component - shows colorful initials instead of generic profile pic
 */
const InitialsAvatar: React.FC<InitialsAvatarProps> = memo(({
  name,
  phone,
  size = 40,
  fontSize,
  style
}) => {
  const initials = getInitials(name, phone);
  const backgroundColor = stringToColor(name || phone || '');
  const calculatedFontSize = fontSize || Math.floor(size * 0.4);

  return (
    <div
      style={{
        width: size,
        height: size,
        borderRadius: '50%',
        backgroundColor,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: '#ffffff',
        fontWeight: 700,
        fontSize: calculatedFontSize,
        fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
        letterSpacing: '0.5px',
        textTransform: 'uppercase',
        flexShrink: 0,
        boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
        border: '2px solid rgba(255, 255, 255, 0.9)',
        ...style
      }}
      title={name || phone || 'Unknown'}
    >
      {initials}
    </div>
  );
});

InitialsAvatar.displayName = 'InitialsAvatar';

export default InitialsAvatar;
